import React, { useRef, useEffect, useCallback } from 'react';
import { useGazeData } from "../../hooks/useGazeData";
import './spotlight-cards-example.scss';

export function SpotlightCardsExample() {
  const items = [
    { t: 'X-rays', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-xrays.png' },
    { t: 'Worms', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-worms.png' },
    { t: 'Aurora', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-aurora.png' },
    { t: 'Angus', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-angus.png' },
    { t: 'Huitzi', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-huitzi.png' },
    { t: 'Dalí', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-dali.png' },
    { t: 'The Bride', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-bride.png' },
    { t: 'The Man', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-man.png' },
    { t: 'D', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-d.png' },
    { t: 'V', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-v.png' },
    { t: 'V II', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-v2.png' },
    { t: 'V III', i: 'https://s3-us-west-2.amazonaws.com/s.cdpn.io/9473/i-v3.png' }
  ];

  const itemRefs = useRef([]);
  const svgRefs = useRef([]);
  const clipRefs = useRef([]);
  const currentGazedIndex = useRef(null);

  // Create SVG point for coordinate transformation
  const createSVGPoint = useCallback((svg) => svg.createSVGPoint(), []);

  // Get coordinates relative to SVG
  const getCoordinates = useCallback((clientX, clientY, svg) => {
    const point = createSVGPoint(svg);
    point.x = clientX;
    point.y = clientY;
    return point.matrixTransform(svg.getScreenCTM().inverse());
  }, [createSVGPoint]);

  // Update clip circle position
  const updateClip = useCallback((clip, coordinates) => {
    clip.setAttribute('cx', coordinates.x);
    clip.setAttribute('cy', coordinates.y);
  }, []);

  // Reusable handler to update clip position based on clientX/clientY
  const moveHandler = useCallback((index, clientX, clientY) => {
    const svg = svgRefs.current[index];
    const clip = clipRefs.current[index];

    if (svg && clip) {
      const coordinates = getCoordinates(clientX, clientY, svg);
      updateClip(clip, coordinates);
    }
  }, [getCoordinates, updateClip]);

  useEffect(() => {
    // Combined event handler for both mouse and touch events
    const handleMove = (index) => (e) => {
      // For touch events, prevent default and get the first touch
      const eventSource = e.type === 'touchmove'
        ? (e.preventDefault(), e.targetTouches[0])
        : e;

      if (!eventSource) return;

      moveHandler(index, eventSource.clientX, eventSource.clientY);
    };

    // Attach event listeners to each item
    const handlers = [];
    itemRefs.current.forEach((item, index) => {
      if (item) {
        const moveHandler = handleMove(index);

        item.addEventListener('mousemove', moveHandler);
        item.addEventListener('touchmove', moveHandler, { passive: false });

        handlers.push({ item, moveHandler });
      }
    });

    // Cleanup event listeners on unmount
    return () => {
      handlers.forEach(({ item, moveHandler }) => {
        item.removeEventListener('mousemove', moveHandler);
        item.removeEventListener('touchmove', moveHandler);
      });
    };
  }, [moveHandler]);

  useGazeData(({ x, y }) => {
    let newGazedIndex = null;

    // Find which item is being gazed at
    for (let index = 0; index < itemRefs.current.length; index++) {
      const item = itemRefs.current[index];
      if (item) {
        const rect = item.getBoundingClientRect();
        // Check if gaze point is within this item's bounds
        if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
          newGazedIndex = index;

          // Update clip position for the gazed item
          const svg = svgRefs.current[index];
          const clip = clipRefs.current[index];
          if (svg && clip) {
            const coordinates = getCoordinates(x, y, svg);
            updateClip(clip, coordinates);
          }
          break; // Found the gazed item, no need to continue
        }
      }
    }

    // Handle state transitions
    if (newGazedIndex !== currentGazedIndex.current) {
      // Remove gazeover class from previous item
      if (currentGazedIndex.current !== null) {
        itemRefs.current[currentGazedIndex.current]?.classList.remove('gazeover');
      }

      // Add gazeover class to new item
      if (newGazedIndex !== null) {
        itemRefs.current[newGazedIndex]?.classList.add('gazeover');
      }

      currentGazedIndex.current = newGazedIndex;
    }
  });

  return (
    <main className="spotlight-example" data-tdx-mouseless>
      <div className="items">
        {items.map((item, index) => (
          <div
            className="item"
            key={index}
            ref={(el) => (itemRefs.current[index] = el)}
          >
            <svg
              viewBox="0 0 300 200"
              preserveAspectRatio="xMidYMid slice"
              ref={(el) => (svgRefs.current[index] = el)}
            >
              <defs>
                <clipPath id={`clip-${index}`}>
                  <circle
                    cx="0"
                    cy="0"
                    r="150px"
                    fill="#000"
                    ref={(el) => (clipRefs.current[index] = el)}
                  />
                </clipPath>
              </defs>
              <text className="svg-text" x="50%" y="50%" dy=".3em">
                {item.t}
              </text>
              <g clipPath={`url(#clip-${index})`}>
                <image
                  xlinkHref={item.i}
                  width="100%"
                  height="100%"
                  preserveAspectRatio="xMinYMin slice"
                />
                <text className="svg-masked-text" x="50%" y="50%" dy=".3em">
                  {item.t}
                </text>
              </g>
            </svg>
          </div>
        ))}
      </div>
    </main>
  );
};